import { Fragment } from "react";
import { gsap } from "gsap";
import ReactTypingEffect from "react-typing-effect";
import { Popover, Transition } from "@headlessui/react";
import { GooglePlayLogo, AppStoreLogo } from "phosphor-react";
import {
  ArrowPathIcon,
  Bars3Icon,
  BookmarkSquareIcon,
  CalendarIcon,
  ChartBarIcon,
  CursorArrowRaysIcon,
  LifebuoyIcon,
  PhoneIcon,
  PlayIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import "./App.css";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import Sell from "./components/sell";
import How from "./components/how";
import Footer from "./components/footer";

const features = [
  {
    name: "Analytics",
    href: "#",
    description:
      "Get a better understanding of where your traffic is coming from.",
    icon: ChartBarIcon,
  },
  {
    name: "Engagement",
    href: "#",
    description: "Speak directly to your customers in a more meaningful way.",
    icon: CursorArrowRaysIcon,
  },
  {
    name: "Security",
    href: "#",
    description: "Your customers' data will be safe and secure.",
    icon: ShieldCheckIcon,
  },
  {
    name: "Integrations",
    href: "#",
    description: "Connect with third-party tools that you're already using.",
    icon: Squares2X2Icon,
  },
  {
    name: "Automations",
    href: "#",
    description:
      "Build strategic funnels that will drive your customers to convert",
    icon: ArrowPathIcon,
  },
];
const callsToAction = [
  { name: "Watch Demo", href: "#", icon: PlayIcon },
  { name: "Contact Sales", href: "#", icon: PhoneIcon },
];
const resources = [
  {
    name: "Help Center",
    description:
      "Get all of your questions answered in our forums or contact support.",
    href: "#",
    icon: LifebuoyIcon,
  },
  {
    name: "Guides",
    description:
      "Learn how to maximize our platform to get the most out of it.",
    href: "#",
    icon: BookmarkSquareIcon,
  },
  {
    name: "Events",
    description:
      "See what meet-ups and other events we might be planning near you.",
    href: "#",
    icon: CalendarIcon,
  },
  {
    name: "Security",
    description: "Understand how we take your privacy seriously.",
    href: "#",
    icon: ShieldCheckIcon,
  },
];
const recentPosts = [
  { id: 1, name: "Boost your conversion rate", href: "#" },
  {
    id: 2,
    name: "How to use search engine optimization to drive traffic to your site",
    href: "#",
  },
  { id: 3, name: "Improve your customer experience", href: "#" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function App() {
  return (
    <div className="relative">
      <Popover className="relative bg-white  ">
        <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 ">
          <div className="flex items-center justify-between py-6 md:justify-start md:space-x-10">
            <div className="flex justify-start lg:w-0 lg:flex-1">
              <a href="#">
                <span className="sr-only">smallbee</span>
                <img
                  className="h-16 w-auto "
                  src="images/smallbee.svg"
                  alt=""
                />
              </a>
            </div>
            <div className="-my-2 -mr-2 md:hidden">
              <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                <span className="sr-only">Open menu</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>
            <Popover.Group as="nav" className="hidden space-x-10 md:flex">
              <Popover className="relative">
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-900",
                        "group inline-flex items-center rounded-md bg-white text-lg  hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      )}
                    >
                      <span>Solutions</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-600" : "text-gray-400",
                          "ml-2 h-5 w-5 group-hover:text-gray-500"
                        )}
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-10 -ml-4 mt-3 w-screen max-w-md transform px-2 sm:px-0 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                            {features.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                              >
                                <item.icon
                                  className="h-6 w-6 flex-shrink-0 text-indigo-600"
                                  aria-hidden="true"
                                />
                                <div className="ml-4">
                                  <p className="text-lg font-medium text-gray-900">
                                    {item.name}
                                  </p>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {item.description}
                                  </p>
                                </div>
                              </a>
                            ))}
                          </div>
                          <div className="space-y-6 bg-gray-50 px-5 py-5 sm:flex sm:space-y-0 sm:space-x-10 sm:px-8">
                            {callsToAction.map((item) => (
                              <div key={item.name} className="flow-root">
                                <a
                                  href={item.href}
                                  className="-m-3 flex items-center rounded-md p-3 text-lg font-medium text-gray-900 hover:bg-gray-100"
                                >
                                  <item.icon
                                    className="h-6 w-6 flex-shrink-0 text-gray-400"
                                    aria-hidden="true"
                                  />
                                  <span className="ml-3">{item.name}</span>
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>

              <a
                href="#"
                className="text-lg font-medium text-gray-900 hover:text-gray-600"
              >
                Pricing
              </a>

              <Popover className="relative">
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-900",
                        "group inline-flex items-center rounded-md bg-white text-lg font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      )}
                    >
                      <span>More</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-900" : "text-gray-900",
                          "ml-2 h-5 w-5 group-hover:text-gray-500"
                        )}
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute left-1/2 z-10 mt-3 w-screen max-w-md -translate-x-1/2 transform px-2 sm:px-0">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                            {resources.map((item) => (
                              <a
                                key={item.name}
                                href={item.href}
                                className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                              >
                                <item.icon
                                  className="h-6 w-6 flex-shrink-0 text-indigo-600"
                                  aria-hidden="true"
                                />
                                <div className="ml-4">
                                  <p className="text-lg font-medium text-gray-900">
                                    {item.name}
                                  </p>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {item.description}
                                  </p>
                                </div>
                              </a>
                            ))}
                          </div>
                          <div className="bg-gray-50 px-5 py-5 sm:px-8 sm:py-8">
                            <div>
                              <h3 className="text-lg font-medium text-gray-500">
                                Recent Posts
                              </h3>
                              <ul role="list" className="mt-4 space-y-4">
                                {recentPosts.map((item) => (
                                  <li
                                    key={item.id}
                                    className="truncate text-lg"
                                  >
                                    <a
                                      href={item.href}
                                      className="font-medium text-gray-900 hover:text-gray-700"
                                    >
                                      {item.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="mt-5 text-sm">
                              <a
                                href="#"
                                className="font-medium text-indigo-600 hover:text-indigo-500"
                              >
                                View all posts
                                <span aria-hidden="true"> &rarr;</span>
                              </a>
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            </Popover.Group>
            <div className="hidden items-center justify-end md:flex md:flex-1 lg:w-0">
              <a
                href="#"
                className="whitespace-nowrap text-lg font-medium text-gray-500 hover:text-gray-900"
              >
                Sign in
              </a>
              <a
                href="#"
                className="ml-8 inline-flex items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-gray-900 px-4 py-2 text-lg font-medium text-white shadow-sm hover:bg-gray-700"
              >
                Sign up
              </a>
            </div>
          </div>
        </div>

        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            focus
            className="absolute inset-x-0 top-0 z-10 origin-top-right transform p-2 transition md:hidden"
          >
            <div className="divide-y-2 divide-gray-50 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="px-5 pt-5 pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <img
                      className="h-8 w-auto"
                      src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
                      alt="Your Company"
                    />
                  </div>
                  <div className="-mr-2">
                    <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </Popover.Button>
                  </div>
                </div>
                <div className="mt-6">
                  <nav className="grid gap-y-8">
                    {features.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="-m-3 flex items-center rounded-md p-3 hover:bg-gray-50"
                      >
                        <item.icon
                          className="h-6 w-6 flex-shrink-0 text-indigo-600"
                          aria-hidden="true"
                        />
                        <span className="ml-3 text-lg font-medium text-gray-900">
                          {item.name}
                        </span>
                      </a>
                    ))}
                  </nav>
                </div>
              </div>
              <div className="space-y-6 py-6 px-5">
                <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                  <a
                    href="#"
                    className="text-lg font-medium text-gray-900 hover:text-gray-700"
                  >
                    Pricing
                  </a>

                  <a
                    href="#"
                    className="text-lg font-medium text-gray-900 hover:text-gray-700"
                  >
                    Docs
                  </a>
                  {resources.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="text-lg font-medium text-gray-900 hover:text-gray-700"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
                <div>
                  <a
                    href="#"
                    className="flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-lg font-medium text-white shadow-sm hover:bg-indigo-700"
                  >
                    Sign up
                  </a>
                  <p className="mt-6 text-center text-lg font-medium text-gray-500">
                    Existing customer?
                    <a
                      href="#"
                      className="text-indigo-600 hover:text-indigo-500"
                    >
                      Sign in
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>

      <main className="lg:relative">
        <div className="mx-auto  max-w-screen-2xl py-12  overflow-x-auto flex gap-12 h-[50rem] ">
          <div className="w-1/2 pl-8   ">
            <div className="block xl:inline font-bold text-5xl leading-snug ">
              Bespoke mobile solutions for your
              <div className="text-orange-700">
                <ReactTypingEffect
                  speed="60"
                  eraseSpeed="60"
                  eraseDelay="2000"
                  typingDelay="400"
                  cursor="_"
                  text={[
                    "business",
                    "yoga studio",
                    "freelance business",
                    "daycare center",
                    "fitness studio",
                  ]}
                />
              </div>
            </div>{" "}
            <div className="mt-52 w-1/2">
              <div className="rounded-md shadow">
                <a
                  href="#"
                  className="flex w-full items-center justify-center rounded-md border border-transparent bg-gray-900 px-8 py-3 text-lg font-bold text-white hover:bg-gray-700 md:py-4 md:px-10 md:text-xl"
                >
                  Get started for free
                </a>
              </div>
              <p className="text-center mt-10 text-lg cursor-pointer ">
                Learn more ↓{" "}
              </p>
            </div>
          </div>
          <div className="flex gap-12 ">
            <img src="images/leap.svg" className="" alt="" />
            <img src="images/hsnc.svg" className="" alt="" />
            <img src="images/bayti.svg" className="" alt="" />
          </div>
        </div>
      </main>

      <section className="lg:relative  bg-gray-200 ">
        <div className="mx-auto max-w-screen-2xl grid grid-cols-4 py-32 gap-12 ">
          <div className=" text-center  col-span-full mb-12">
            <h2 className="font-black text-5xl "> Your Voice. Your Brand.</h2>
            <p className="text-3xl leading-tight mt-2 font-light ">
              Connect with your customers directly with your brand of service.{" "}
            </p>
          </div>
          <div></div>
          <div className="text-xl leading-relaxed ">
            {" "}
            <img src="images/icon.svg" alt="" className="pb-8" />
            <span className="font-bold">Your own mobile app.</span> <br />
            Connect with your customers directly with your brand of service.
            Your customers find you directly from an app on thier phone.
          </div>
          <img src="images/teacher-train.svg" alt="" className="" />
        </div>
      </section>
      <section className="lg:relative  bg-gray-200  ">
        <div className="mx-auto max-w-screen-2xl grid grid-cols-4 py-32 gap-12  ">
          <div className=" text-center col-span-full mb-12">
            <h2 className="font-black text-6xl px-64 leading-snug">
              Structure your services just the way you want.
            </h2>
          </div>
          <img src="images/offer-ui.svg" alt="" />
          <img src="images/peru.svg" alt="" className="" />
          <img src="images/offer2-ui.svg" alt="" />
          <img src="images/chickpea.svg" alt="" className="" />
        </div>
      </section>
      <section className="lg:relative  bg-gray-200 ">
        <div className="mx-auto max-w-screen-2xl grid grid-cols-4 py-32 gap-12  ">
          <div className=" text-center  col-span-full mb-12 ">
            <h2 className="font-black text-5xl "> Mobile First Business</h2>
            <p className="text-3xl leading-tight mt-2 font-light">
              Mobile is personal. So is the outreach.{" "}
            </p>
          </div>
          <div></div>
          <div className="text-xl leading-relaxed ">
            {" "}
            <p className="mt-24">
              All the tools you need to draft and publish the right messaging -
              <b>
                {" "}
                push notifications, sales & deals, announcements, events, etc.
              </b>
            </p>
            <img src="images/msg-ui.svg" alt="" className="mt-6" />
          </div>
          <img src="images/my-schedule.svg" alt="" className="" />

          <div className="pt-64">
            <img src="images/watch.svg" className="w-32" />
          </div>
        </div>
      </section>
      <section className="lg:relative  bg-gray-200 ">
        <div className="mx-auto max-w-screen-2xl grid grid-cols-4 py-32 gap-12  ">
          <div className=" text-center  col-span-full mb-12 ">
            <h2 className="font-black text-5xl ">
              {" "}
              Usage Analytics + Actionable Insights
            </h2>
            <p className="text-3xl leading-tight mt-2 font-light">
              Make informed decisions with insights into your customers
              interactions, sales, bookings, communications, growth trends, POS
              interactions, etc.{" "}
            </p>
          </div>
          <img src="images/metrics1.svg" alt="" />
          <img src="images/msg-ui.svg" alt="" />
          <img src="images/msg-ui.svg" alt="" />
          <img src="images/msg-ui.svg" alt="" />
          <img src="images/metrics1.svg" alt="" />
          <img src="images/msg-ui.svg" alt="" />
          <img src="images/metrics1.svg" alt="" />
          <img src="images/msg-ui.svg" alt="" />
        </div>
      </section>

      <section className="lg:relative  ">
        <h2 className="font-black  text-center text-6xl mt-24 ">
          How does it work?
        </h2>
        <div className="mx-auto max-w-screen-xl grid grid-cols-3  gap-12 mt-24 mb-32    ">
          <div className="shadow-lg p-12 rounded-xl border">
            <h3 className="font-light text-3xl  rounded-full  text-black   ">
              Step 1
            </h3>
            <ul className="text-xl  mt-12 ">
              <li className="font-bold">
                Start by telling us a little about your business.
              </li>
            </ul>
            <p className="text-lg  mt-12  bg-yellow-100 rounded-xl p-6 flex leading-snug ">
              <img src="images/bee-sm.svg" alt="" className="pr-6" />
              We will start you off with a set of starter modules.
            </p>
          </div>
          <div className="shadow-lg p-12 rounded-xl border">
            <h3 className="font-light text-3xl  rounded-full  text-black  ">
              Step 2
            </h3>
            <ul className=" font-normal mt-12 leading-relaxed ">
              <li className=" pb-8 text-xl font-bold">
                Add, remove or edit service modules as you see fit.
              </li>
              <li className=" pb-8 text-lg">
                Save, share and gather feedback with your team members.
              </li>
              <li className="text-lg">
                Hit the 'looks good' button when you are ready. <br />{" "}
                <b className="font-bold">Thats it!</b>
              </li>
            </ul>

            <p className="text-lg  mt-12  bg-yellow-100 rounded-xl p-6 flex leading-snug">
              <img src="images/bee-sm.svg" alt="" className="pr-6" />
              Seek help from a SB expert at any point in time.
            </p>
          </div>
          <div className="shadow-lg p-12 rounded-xl border">
            <h3 className="font-light text-3xl  rounded-full  text-black  ">
              Step 3
            </h3>

            <p className="text-lg  mt-12  bg-yellow-100 rounded-xl p-6 ">
              <img src="images/bee-sm.svg" alt="" className="pb-6" />A dedicated
              SB team will work with you on:{" "}
              <ul className=" font-normal mt-2 leading-relaxed px-4 text-sm">
                <li className="list-disc">branding and content</li>
                <li className="list-disc">
                  domains, contracts and hosting logistics
                </li>
                <li className="list-disc">team onboarding and training</li>
              </ul>
            </p>
            <p className="text-xl font-bold mt-12 leading-snug ">
              Download your app from iPhone App Store and Google Play store.
            </p>
            <div className="flex mt-6 gap-6">
              <GooglePlayLogo size={32} weight="fill" />
              <AppStoreLogo size={32} weight="fill" />
            </div>
          </div>
        </div>
      </section>

      <section className="lg:relative bg-black text-white  ">
        <div className=" max-w-xl text-center pt-24 mx-auto">
          <h2 className="font-black text-6xl">Start for free.</h2>
          <p className="text-3xl mt-6 mb-12 text-gray-400">
            Design and launch the digital version of your business today!
          </p>
          <button className="rounded-md bg-gray-100 text-black text-xl font-bold px-8 py-3">
            Start for free
          </button>
        </div>

        <div className="mx-auto max-w-screen-2xl grid grid-cols-4 py-32 gap-12  text-white  ">
          <div>
            <img src="images/smallbee.svg" alt="" />
          </div>
          <div>
            {" "}
            <h4 className="font-medium text-xl mb-4">Company</h4>
            <ul>
              <li className="mb-2">About</li>
              <li className="mb-2">Team</li>
              <li className="mb-2">Terms</li>
              <li className="mb-2">Security</li>
            </ul>
          </div>
          <div>
            {" "}
            <h4 className="font-medium text-xl mb-4">Service</h4>
            <ul>
              <li className="mb-2">About</li>
              <li className="mb-2">Team</li>
              <li className="mb-2">Terms</li>
              <li className="mb-2">Security</li>
            </ul>
          </div>
          <div>
            {" "}
            <h4 className="font-medium text-xl">Service</h4>
            <ul>
              <li>FAQs</li>
              <li>Terms</li>
              <li>Security</li>
              <li>Privacy</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
